import { useCallback } from 'react';
import {
  Listbox,
  Modal,
  ModalHeader,
  ModalBody,
  Heading,
  useToast,
} from '@kinesis/bungle';
import { Intervention, ScenarioWithInventory } from '@/types';
import {
  useGetAllScenariosQuery,
  useLazyGetScenarioQuery,
  useSaveScenarioMutation,
} from '@/api/scenarios';
import { identify } from '@/data/identify';

const CopyIntervention = (props: CopyInterventionProps) => {
  const { intervention, onClose } = props;
  const { data: scenarios = [] } = useGetAllScenariosQuery();
  const [getScenario] = useLazyGetScenarioQuery();
  const [saveScenario] = useSaveScenarioMutation();
  const toast = useToast('globalTop');

  const options = scenarios.map((s: ScenarioWithInventory) => ({
    value: s.id,
    label: `${s.inventoryName} / ${s.name}`,
  }));

  const onSelect = useCallback(
    async (scenarioId: number) => {
      try {
        const targetScenario = await getScenario(scenarioId).unwrap();
        const copied = identify({
          ...intervention,
          id: undefined,
        });
        const updated = {
          ...targetScenario,
          interventions: [...targetScenario.interventions, copied],
        };
        await saveScenario(updated).unwrap();
        const target = scenarios.find(
          (s: ScenarioWithInventory) => s.id === scenarioId,
        );
        toast(
          `Intervention copied to ${target?.inventoryName} / ${target?.name}`,
          { variant: 'success' },
        );
        onClose();
      } catch {
        toast('Could not copy intervention.', { variant: 'error' });
      }
    },
    [getScenario, saveScenario, intervention, scenarios, toast, onClose],
  );

  return (
    <Modal onClose={onClose}>
      <ModalHeader>
        <Heading as='h3' size='small'>
          Copy intervention to scenario
        </Heading>
      </ModalHeader>
      <ModalBody>
        <Listbox
          search
          options={options}
          labelKey='label'
          valueKey='value'
          onChange={onSelect}
          placeholder='Search scenarios...'
          emptyText='No scenarios found'
          maxHeight={300}
        />
      </ModalBody>
    </Modal>
  );
};

export type CopyInterventionProps = {
  intervention: Intervention;
  onClose: () => void;
};

export { CopyIntervention };
